import Dropdown from "../general/Dropdown.tsx";
import {BankAccountLogo} from "../general/BankAccountLogo.tsx";
import {CustomerBankAccount} from "../../api/customerBankAccounts/CustomerBankAccount.ts";

const ALL_ACCOUNTS_VALUE = "__all_accounts__";

interface BankAccountSelectorProps {
    bankAccounts: CustomerBankAccount[];
    selectedAccountId?: string;
    onSelect: (account: CustomerBankAccount | null) => void;
    label?: string;
    allOptionLabel?: string;
    menuClassName?: string;
    variant?: "inline" | "field";
}

export default function BankAccountSelector({
    bankAccounts,
    selectedAccountId,
    onSelect,
    label,
    allOptionLabel,
    menuClassName,
    variant = "inline",
}: BankAccountSelectorProps) {
    const isField = variant === "field";
    const selectedValue = selectedAccountId ?? (allOptionLabel ? ALL_ACCOUNTS_VALUE : "");

    const options = [
        ...(allOptionLabel ? [{value: ALL_ACCOUNTS_VALUE}] : []),
        ...bankAccounts.map(account => ({value: account.id})),
    ];

    function handleSelect(value: string | number) {
        if (value === ALL_ACCOUNTS_VALUE) {
            onSelect(null);
            return;
        }
        const account = bankAccounts.find(acc => acc.id === value);
        if (account) onSelect(account);
    }

    function renderOption({value, isHighlighted, isTrigger}: {value: string | number; isHighlighted?: boolean; isTrigger?: boolean}) {
        if (value === ALL_ACCOUNTS_VALUE) {
            return (
                <span className={`font-medium text-sm ${isHighlighted ? 'text-white' : ''}`}>
                    {allOptionLabel}
                </span>
            );
        }

        const account = bankAccounts.find(acc => acc.id === value);
        if (!account) return <span>{value}</span>;

        const identifier = account.displayIdentifier();

        if (isField && isTrigger) {
            return (
                <span className="flex min-w-0 items-center gap-2 overflow-hidden">
                    <BankAccountLogo logoUrl={account.logoUrl} accountType={account.accountType} creditCardNumberPrefix={account.creditCardNumberPrefix} className="h-5 w-5 shrink-0" />
                    <span className="min-w-0 truncate font-medium">{account.displayInstitutionName()}</span>
                    {identifier && <span className="shrink-0 whitespace-nowrap text-xs text-gray-500">{identifier}</span>}
                </span>
            );
        }

        return (
            <div className={`flex items-center gap-3 ${isField ? 'min-w-0' : ''}`}>
                <BankAccountLogo logoUrl={account.logoUrl} accountType={account.accountType} creditCardNumberPrefix={account.creditCardNumberPrefix} className={`h-6 w-6 ${isField ? 'shrink-0' : ''}`} />
                <div className={`flex flex-col ${isField ? 'min-w-0' : ''}`}>
                    <span className={`font-medium text-sm ${isField ? 'truncate' : ''} ${isHighlighted ? 'text-white' : ''}`}>
                        {account.displayInstitutionName()}
                    </span>
                    <span className={`text-xs ${isField ? 'truncate' : 'whitespace-nowrap'} ${isHighlighted ? 'text-blue-100' : 'text-gray-500'}`}>
                        {account.ownerName} • {account.currency}
                        {identifier && ` • ${identifier}`}
                    </span>
                </div>
            </div>
        );
    }

    if (bankAccounts.length === 0 && !allOptionLabel) {
        return (
            <div className="flex items-center gap-x-3">
                {label && `${label}:`}
                <span className="text-gray-500 italic">Geen bankrekeningen beschikbaar</span>
            </div>
        );
    }

    if (isField) {
        return (
            <Dropdown
                menuClassName={menuClassName ?? "w-[max(var(--button-width),32rem)]"}
                selected={{value: selectedValue}}
                options={options}
                customItemRenderFn={renderOption}
                onUpdateFunc={handleSelect}
            />
        );
    }

    return (
        <div className="flex items-center gap-x-3">
            {label && `${label}:`}
            <div className={selectedValue ? "" : "hidden"}>
                <Dropdown
                    menuClassName={menuClassName ?? "w-[32rem]"}
                    selected={{value: selectedValue}}
                    options={options}
                    customItemRenderFn={renderOption}
                    onUpdateFunc={handleSelect}
                />
            </div>
        </div>
    );
}
